// Materials Science module (WASM-powered)
import init, {
  get_materials_defaults, run_bandgap_scan, run_elastic_scan,
  run_phase_scan, run_thermal_scan, run_defect_scan
} from './engines.js';
import { getLicense, hasFullAccess } from './auth.js';
import { GeoNum, geo } from './geo-num.js';
import { ZONES_MATERIALS, ZONES_THEORY } from './geo-num-zones.js';
import { scaleEmissionScan, seSetMode } from './scale-emission.js';
import { registerReport } from './report-gen.js';

const _MODULE = 'Materials';
const charts = {};
let ready = false, defaults = null;

// Animation state
const animData = {};

// ============================================================
// Superconductor T_c Predictor — σ₅ Framework
// ============================================================
const SIGMA5 = 0.09017;
const K_B_EV = 8.617333262e-5;               // eV/K
const O_PRIMARY = SIGMA5 / (2 + SIGMA5);     // 0.04314 — magnetically active fraction
const GEOM_TC = (SIGMA5 * SIGMA5) / (2 + SIGMA5); // 0.003890 — geometric T_c coefficient

const KNOWN_CUPRATES = [
  { name: 'YBCO',       formula: 'YBa\u2082Cu\u2083O\u2087',          ect: 2.00, tc_k: 93,  family: 'Y-123' },
  { name: 'Bi-2212',    formula: 'Bi\u2082Sr\u2082CaCu\u2082O\u2088',  ect: 1.80, tc_k: 85,  family: 'Bi-2212' },
  { name: 'LBCO',       formula: 'La\u2081.\u2088\u2085Ba\u2080.\u2081\u2085CuO\u2084', ect: 0.85, tc_k: 38, family: 'La-214' },
  { name: 'Tl-2201',    formula: 'Tl\u2082Ba\u2082CuO\u2086',          ect: 2.10, tc_k: 95,  family: 'Tl-220n' },
  { name: 'Hg-1201',    formula: 'HgBa\u2082CuO\u2084',                ect: 2.00, tc_k: 98,  family: 'Hg-120n' },
  { name: 'BSCCO-2223', formula: 'Bi\u2082Sr\u2082Ca\u2082Cu\u2083O\u2081\u2080', ect: 2.30, tc_k: 110, family: 'Bi-2223' },
];

// ============================================================
// Alloy Design — σ₅ Magnetic Coupling
// ============================================================
const ALLOY_BASES = {
  Fe: { name: 'Iron',   tc0: 1043, crystal: 'BCC', mu_b: 2.22,  hc0: 40,  M_gmol: 55.845, rho0: 9.71e-8, drho: 1.4e-7 },
  Ni: { name: 'Nickel', tc0: 627,  crystal: 'FCC', mu_b: 0.606, hc0: 160, M_gmol: 58.693, rho0: 6.99e-8, drho: 8.0e-8 },
  Co: { name: 'Cobalt', tc0: 1388, crystal: 'HCP', mu_b: 1.72,  hc0: 80,  M_gmol: 58.933, rho0: 6.24e-8, drho: 9.0e-8 },
};
const ALLOY_DOPANTS = {
  C:  { name: 'Carbon',    M_gmol: 12.011, k_factor: 1.000, E_pin_ev: 0.80, desc: 'Interstitial — strong pinning' },
  Mn: { name: 'Manganese', M_gmol: 54.938, k_factor: 0.450, E_pin_ev: 0.35, desc: 'Substitutional — moderate dilution' },
  Si: { name: 'Silicon',   M_gmol: 28.086, k_factor: 0.380, E_pin_ev: 0.25, desc: 'Substitutional — soft magnetic aid' },
  Cr: { name: 'Chromium',  M_gmol: 51.996, k_factor: 0.520, E_pin_ev: 0.40, desc: 'Substitutional — stainless grade' },
};
// C_PIN calibrated: Fe + 0.2%C → H_c = 342 A/m (measured 200-400 A/m)
// 342 - 40 = 302 = C_PIN × 0.2^(2/3) × 0.04314 × 0.8 → C_PIN ≈ 25572
const C_PIN = 25572;

// Canvas animation handles (one per canvas id)
const animHandles = {};

export async function initWasm() {
  try {
    await init();
    ready = true;
    defaults = JSON.parse(get_materials_defaults());
    populateDefaults();
    // Configure GeoNum for materials domain (Angstrom lattice scale)
    GeoNum.setZoneConfig(ZONES_MATERIALS.boundaries, ZONES_MATERIALS.minZone);
  }
  catch (err) { console.error('WASM init (materials):', err); }
}

export function init_ui() {
  document.querySelectorAll('.mattab').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.mattab').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.materials-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById('matp-' + btn.dataset.mat).classList.add('active');
    });
  });
  bind('btn-bandgap', bandgapScan);
  bind('btn-elastic', elasticScan);
  bind('btn-phase', phaseScan);
  bind('btn-thermal', thermalScan);
  bind('btn-defect', defectScan);
  // Pure-JS tabs (no WASM required)
  bindJS('btn-superconductor', superconductorScan);
  bindJS('btn-alloy', alloyDesignScan);
  bindJS('btn-scale-emission', scaleEmissionScan);

  // Superconductor mode toggle
  const scMode = document.getElementById('sc-mode');
  if (scMode) {
    scMode.addEventListener('change', () => {
      const m = scMode.value;
      document.getElementById('sc-forward-inputs')?.classList.toggle('hidden', m !== 'forward');
      document.getElementById('sc-inverse-inputs')?.classList.toggle('hidden', m !== 'inverse');
    });
  }
  // Scale Emission mode toggle
  const seMode = document.getElementById('se-mode');
  if (seMode) seMode.addEventListener('change', () => seSetMode(seMode.value));
  // Alloy mode toggle
  const alMode = document.getElementById('al-mode');
  if (alMode) {
    alMode.addEventListener('change', () => {
      const m = alMode.value;
      document.getElementById('al-forward-inputs')?.classList.toggle('hidden', m !== 'forward');
      document.getElementById('al-inverse-inputs')?.classList.toggle('hidden', m !== 'inverse');
    });
  }
}

export function applyMaterialsGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const has = hasFullAccess(tier) || license?.modules?.includes('materials') || (tier === 'trial' && license?.isActive);
  const g = document.getElementById('mat-gate');
  const c = document.getElementById('mat-content');
  if (g && c) {
    if (has) { g.classList.add('hidden'); c.classList.remove('hidden'); }
    else { g.classList.remove('hidden'); c.classList.add('hidden'); }
  }
}

function populateDefaults() {
  if (!defaults) return;
  setTA('in-bandgap', defaults.bandgap?.elements);
  setTA('in-elastic', defaults.elastic?.materials);
  setTA('in-phase', defaults.phase?.phases);
  setTA('in-thermal', defaults.thermal?.materials);
  setTA('in-defect', defaults.defect?.materials);
}

// --- helpers ---
function setTA(id, v) { const el = document.getElementById(id); if (el && v) el.value = JSON.stringify(v, null, 2); }
function bind(id, fn) { const el = document.getElementById(id); if (el) el.addEventListener('click', async () => { if (!ready) return; el.disabled = true; window.gdbsShowLoading && window.gdbsShowLoading('Computing...', (el.textContent || '').trim()); await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r))); try { await fn(); } finally { window.gdbsHideLoading && window.gdbsHideLoading(); el.disabled = false; } }); }
function bindJS(id, fn) { const el = document.getElementById(id); if (el) el.addEventListener('click', async () => { el.disabled = true; window.gdbsShowLoading && window.gdbsShowLoading('Computing...', (el.textContent || '').trim()); await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r))); try { await fn(); } finally { window.gdbsHideLoading && window.gdbsHideLoading(); el.disabled = false; } }); }
function status(id, m) { const el = document.getElementById('s-' + id); if (el) el.textContent = m; }
function fmtSci(v) { return typeof v === 'number' ? (Math.abs(v) > 1e6 || (Math.abs(v) < 0.001 && v !== 0) ? v.toExponential(3) : v.toFixed(4)) : v; }
function tierColor(val) {
  const v = parseFloat(val); let r, g, b;
  if (v < 0.5) { const t = v / 0.5; r = 220; g = Math.round(50 + 170 * t); b = 50; }
  else { const t = (v - 0.5) / 0.5; r = Math.round(220 - 180 * t); g = 200; b = Math.round(50 + 30 * t); }
  return `rgb(${r},${g},${b})`;
}
function renderGradientBar(tc) {
  if (!tc) return '';
  const base = [{ val: tc.core, name: 'Core' }, { val: tc.magnitude, name: 'Mag' }, { val: tc.phase, name: 'Phase' }, { val: tc.proportion, name: 'Prop' }];
  const rel = [];
  if (tc.relational) { for (let i = 0; i < tc.relational.length; i++) { const r = tc.relational[i], l = i + 2; rel.push({ val: r[0], name: `Mag\u00D7${l}` }, { val: r[1], name: `Phase\u00D7${l}` }, { val: r[2], name: `Prop\u00D7${l}` }); } }
  const all = [...base, ...rel], pct = (100 / all.length).toFixed(2);
  let h = '<div class="tc-wrap"><div class="tc-bar">';
  for (const t of all) h += `<div class="tc-seg" style="width:${pct}%;background:${tierColor(Math.max(0.08, t.val))}"><span class="tc-tip">${t.name}<br>${t.val.toFixed(3)}</span></div>`;
  h += '</div><div class="tc-labels">'; for (const t of all) h += `<span class="tc-lbl" style="width:${pct}%">${t.val.toFixed(2)}</span>`;
  return h + '</div></div>';
}
function renderTable(id, headers, rows) {
  let h = '<thead><tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr></thead><tbody>';
  for (const r of rows) h += '<tr>' + r.map(v => '<td>' + fmtSci(v) + '</td>').join('') + '</tr>';
  const el = document.getElementById('t-' + id); if (el) el.innerHTML = h + '</tbody>';
}
/** Build a verify popover icon that shows reference details on hover */
function verifyLink(url, label, refVal, refSrc) {
  if (!url && !refSrc) return '';
  const popId = 'vp-' + Math.random().toString(36).slice(2, 8);
  const linkHtml = url ? `<a href="${url}" target="_blank" rel="noopener" style="color:#4fc3f7;text-decoration:underline;">Open source ↗</a>` : '';
  const valHtml = refVal ? `<div style="margin:4px 0;"><strong style="color:#e2e8f0;">${refVal}</strong></div>` : '';
  const srcHtml = refSrc ? `<div style="font-size:10px;color:#94a3b8;">${refSrc}</div>` : '';
  return ` <span class="ref-verify" style="position:relative;cursor:pointer;" onmouseenter="document.getElementById('${popId}').style.display='block'" onmouseleave="document.getElementById('${popId}').style.display='none'">` +
    `<span style="color:#4fc3f7;font-size:11px;border:1px solid rgba(79,195,247,0.3);border-radius:3px;padding:0 3px;">ref</span>` +
    `<div id="${popId}" style="display:none;position:absolute;left:0;top:20px;z-index:999;background:#0d1117;border:1px solid rgba(79,195,247,0.3);border-radius:6px;padding:10px 14px;min-width:220px;box-shadow:0 8px 24px rgba(0,0,0,0.6);font-size:12px;line-height:1.5;white-space:nowrap;">` +
    `<div style="font-weight:600;color:#4fc3f7;margin-bottom:4px;">📋 ${label}</div>` +
    valHtml + srcHtml +
    `<div style="margin-top:6px;">${linkHtml}</div>` +
    `</div></span>`;
}
function makeChart(id, labels, datasets, xLbl, yLbl, type) {
  if (charts[id]) charts[id].destroy();
  const c = document.getElementById('c-' + id); if (!c) return;
  charts[id] = new Chart(c.getContext('2d'), { type: type || 'bar', data: { labels, datasets },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      scales: { x: { title: { display: true, text: xLbl } }, y: { title: { display: true, text: yLbl } } },
      plugins: { legend: { position: 'top' } } } });
  try { const _s=datasets.map(ds=>({label:ds.label??'Series',color:ds.borderColor??(typeof ds.backgroundColor==='string'?ds.backgroundColor:'#2563eb'),dashed:!!(ds.borderDash&&ds.borderDash.length),data:labels?labels.map((l,i)=>{const v=ds.data[i];return[typeof l==='number'?l:i,v!=null&&typeof v==='object'?(v.y??0):(v??0)];}):ds.data.map(p=>Array.isArray(p)?p:(p&&typeof p==='object'?[p.x??0,p.y??0]:[0,p??0]))}));registerReport({title:id,module:_MODULE,params:[],series:_s,xLabel:xLbl,yLabel:yLbl});} catch(_){}
}

// ============================================================
// 3D viewport adapter — map a scan's per-material scalars to a
// 3D scatter (one point per material, z = headline value, coloured
// by value). Sets window.__gdbs3dPayload so the global 3D button
// and the per-panel "View in 3D" button can open it.
// ============================================================
function set3DPayload(scan, names, values, title) {
  if (!names || !names.length) return;
  const n = names.length;
  const pts = [];
  const vals = [];
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) { const v = Number(values[i]) || 0; if (v < lo) lo = v; if (v > hi) hi = v; }
  const span = (hi - lo) || 1;
  for (let i = 0; i < n; i++) {
    const v = Number(values[i]) || 0;
    // arrange materials in a ring on the x-y plane, lift z by normalised value
    const a = (n > 1) ? (i / n) * Math.PI * 2 : 0;
    const r = (n > 1) ? 1.0 : 0.0;
    pts.push(Math.cos(a) * r, Math.sin(a) * r, (v - lo) / span);
    vals.push(v);
  }
  const payload = { points: new Float32Array(pts), values: vals, title: title || 'Materials field' };
  window.__gdbs3dPayload = payload;
  // attach / refresh a "View in 3D" button next to this scan's chart
  add3DButton('matp-' + scan, scan);
  return payload;
}

// Phillips-Van Vechten heteronuclear gap model (mirrors wasm/src/materials/
// bandgap.rs constants) so we can render a DENSE continuous gap landscape, not
// a sparse 15-point scatter. E_g as a function of electronegativity difference
// dchi and bond length d (Angstrom).
function bandgapModelEg(dchi, d) {
  const A_H = 23.0, P_H = 2.5, A_C = 1.5, K_S = 1.0, C_H = 5.0,
        ALPHA = 0.75, BETA = 10.0, DELTA = 1.45, D_REF = 2.0, AA = 1.01, AB = 0.10;
  const fi = (dchi * dchi) / (dchi * dchi + C_H);
  const e_h = A_H / Math.pow(d, P_H);
  const e_c = A_C * dchi * Math.exp(-K_S * d / 4);
  const raw = Math.hypot(e_h, e_c);
  const cov = 1 - ALPHA * Math.exp(-BETA * fi);
  const dcorr = Math.pow(d / D_REF, DELTA);
  return AA * (raw * cov * dcorr) + AB;
}

// Dense band-gap landscape: the model surface E_g(dchi, d) over the parameter
// space, PLUS the actual computed materials placed on it at their real
// (dchi, d, gap). Uses the full model and every result, not a single column.
function buildBandgapField(rows, elems) {
  const pts = [], vals = [];
  const DCHI1 = 3.0, D0 = 1.4, D1 = 3.6, NG = 48;
  for (let i = 0; i <= NG; i++) {
    for (let j = 0; j <= NG; j++) {
      const dchi = DCHI1 * i / NG, d = D0 + (D1 - D0) * j / NG;
      const eg = bandgapModelEg(dchi, d);
      if (isFinite(eg)) { pts.push(dchi, d, eg); vals.push(eg); }
    }
  }
  if (Array.isArray(elems) && elems.length >= 2) {
    let k = 0;
    for (let a = 0; a < elems.length; a++) {
      for (let b = a + 1; b < elems.length; b++) {
        const dchi = Math.abs((elems[a].electronegativity || 0) - (elems[b].electronegativity || 0));
        const d = (elems[a].atomic_radius || 0) + (elems[b].atomic_radius || 0);
        const g = rows[k] ? (rows[k].analytical_ev != null ? rows[k].analytical_ev : rows[k].band_gap_ev) : NaN;
        if (isFinite(dchi) && isFinite(d) && isFinite(g)) { pts.push(dchi, d, g); vals.push(g); }
        k++;
      }
    }
  }
  if (pts.length < 9) return;
  window.__gdbs3dPayload = { points: new Float32Array(pts), values: vals,
    title: 'Band-gap landscape: E_g vs electronegativity-difference and bond length (model surface + computed materials)' };
  add3DButton('matp-bandgap', 'bandgap');
}

// Richer adapter: maps EVERY result row into a 3D point using multiple result
// fields (independent axes + a colour metric), so the render uses all of the
// results rather than a single column. ax = {x, y, z, c} accessor functions.
function set3DRich(scan, rows, ax, title) {
  if (!Array.isArray(rows) || !rows.length) return;
  const pts = [], vals = [];
  for (const row of rows) {
    const x = Number(ax.x(row)), y = Number(ax.y(row)), z = Number(ax.z(row));
    if (!isFinite(x) || !isFinite(y) || !isFinite(z)) continue;
    const c = ax.c ? Number(ax.c(row)) : z;
    pts.push(x, y, z);
    vals.push(isFinite(c) ? c : z);
  }
  if (pts.length < 9) return;
  window.__gdbs3dPayload = { points: new Float32Array(pts), values: vals, title: title || 'Materials' };
  add3DButton('matp-' + scan, scan);
}

// Superconductor T_c landscape: the closed-form sigma5 model
// T_c = (s^2 / (2+s)) * E_CT / k_B evaluated over a DENSE grid in the charge-
// transfer gap E_CT and the framework coefficient sigma5, with the computed
// cuprates/candidates marked on the surface at their real (E_CT, sigma5, T_c).
// marks = [{ ect, sigma, tc }].
function buildSuperconductorField(marks, title) {
  const pts = [], vals = [];
  const E0 = 0.3, E1 = 2.8, S0 = 0.05, S1 = 0.14, NG = 56;
  for (let i = 0; i <= NG; i++) {
    for (let j = 0; j <= NG; j++) {
      const ect = E0 + (E1 - E0) * i / NG;
      const s = S0 + (S1 - S0) * j / NG;
      const tc = (s * s / (2 + s)) * ect / K_B_EV;
      if (isFinite(tc)) { pts.push(ect, s, tc); vals.push(tc); }
    }
  }
  if (Array.isArray(marks)) {
    for (const m of marks) {
      const ect = Number(m.ect), s = Number(m.sigma), tc = Number(m.tc);
      if (isFinite(ect) && isFinite(s) && isFinite(tc)) { pts.push(ect, s, tc); vals.push(tc); }
    }
  }
  if (pts.length < 9) return;
  window.__gdbs3dPayload = { points: new Float32Array(pts), values: vals,
    title: title || 'Superconductor T_c landscape: T_c vs charge-transfer gap E_CT and sigma5' };
  add3DButton('matp-superconductor', 'superconductor');
}

// Alloy property landscape: the closed-form sigma5 alloy model swept DENSELY
// over dopant concentration. For each base+dopant pair the Curie-temperature
// depression and the coercivity are exact closed forms in wt%, so we trace
// (x_wt%, T_Curie, H_c) as a dense parametric curve and mark the computed
// operating point(s). marks = [{ xwt, base, dop, label }].
function alloyXmoleFromWt(x_wt, dop, base) {
  const numer = x_wt / dop.M_gmol;
  const denom = numer + (1 - x_wt) / base.M_gmol;
  return denom !== 0 ? numer / denom : 0;
}
function buildAlloyField(pairs, marks, title) {
  const pts = [], vals = [];
  const NG = 160;
  let seriesIndex = 0;
  for (const { base, dop } of pairs) {
    for (let i = 0; i <= NG; i++) {
      const x_wt_pct = 5 * i / NG;            // 0..5 wt%
      const x_wt = x_wt_pct / 100;
      const x_mole = alloyXmoleFromWt(x_wt, dop, base);
      const delta = -(9 / 4) * x_mole * dop.k_factor * base.tc0;
      const tc_curie = Math.max(0, base.tc0 + delta);
      const hc = base.hc0 + C_PIN * Math.pow(x_wt_pct, 2 / 3) * O_PRIMARY * dop.E_pin_ev;
      if (isFinite(tc_curie) && isFinite(hc)) { pts.push(x_wt_pct, tc_curie, hc); vals.push(seriesIndex); }
    }
    seriesIndex++;
  }
  if (Array.isArray(marks)) {
    for (const m of marks) {
      const base = ALLOY_BASES[m.base], dop = ALLOY_DOPANTS[m.dop];
      if (!base || !dop) continue;
      const x_wt = m.xwt / 100;
      const x_mole = alloyXmoleFromWt(x_wt, dop, base);
      const delta = -(9 / 4) * x_mole * dop.k_factor * base.tc0;
      const tc_curie = Math.max(0, base.tc0 + delta);
      const hc = base.hc0 + C_PIN * Math.pow(m.xwt, 2 / 3) * O_PRIMARY * dop.E_pin_ev;
      if (isFinite(tc_curie) && isFinite(hc)) { pts.push(m.xwt, tc_curie, hc); vals.push(seriesIndex); }
    }
  }
  if (pts.length < 9) return;
  window.__gdbs3dPayload = { points: new Float32Array(pts), values: vals,
    title: title || 'Alloy landscape: dopant wt% vs T_Curie vs coercivity (sigma5 model)' };
  add3DButton('matp-alloy', 'alloy');
}

function add3DButton(panelId, scan) {
  const panel = document.getElementById(panelId);
  if (!panel) return;
  const btnId = 'mat3d-' + scan;
  if (document.getElementById(btnId)) return; // already added
  const btn = document.createElement('button');
  btn.id = btnId;
  btn.type = 'button';
  btn.textContent = 'View in 3D';
  btn.className = 'btn btn-sm';
  btn.style.cssText = 'margin:6px 0;';
  btn.addEventListener('click', () => {
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  });
  const tbl = document.getElementById('t-' + scan);
  if (tbl && tbl.parentNode) tbl.parentNode.insertBefore(btn, tbl);
  else panel.appendChild(btn);
}

// GeoNum precision demonstration for materials calculations
function geoNumDemo(value, description) {
  const ieee = value;
  const geoVal = geo(value);
  const geoResult = geoVal.toFloat64();
  const uncertainty = geoVal.getUncertainty();
  const relUncertainty = geoVal.getRelativeUncertainty();

  return `<div class="geonum-result">
    <div class="geonum-label">${description}</div>
    <div class="geonum-comparison">
      <div class="geonum-ieee">IEEE 754: ${ieee.toExponential(8)}</div>
      <div class="geonum-geo">GeoNum: ${geoResult.toExponential(8)} ± ${uncertainty.toExponential(2)}</div>
      <div class="geonum-meta">Zone: ${geoVal.zone} | Shade: ${geoVal.shade} | Drift: ${geoVal.drift.toFixed(4)} | Rel. Unc: ${(relUncertainty * 100).toExponential(2)}%</div>
    </div>
  </div>`;
}

// --- Scans ---

function bandgapScan() {
  status('bandgap', 'Computing...'); const elems = JSON.parse(document.getElementById('in-bandgap').value);
  const res = JSON.parse(run_bandgap_scan(JSON.stringify({ elements: elems, precision: 0 })));
  const r = res.results;
  renderTable('bandgap', ['Name', 'E_g (eV)', 'Model (eV)', 'Ref (eV)', 'Model Err', 'Source', 'Type', 'Ionicity', 'Coherence'], r.map(x => {
    const analytical = x.analytical_ev ?? x.band_gap_ev;
    const refEv = x.ref_gap_ev ?? 0;
    const refUrl = x.ref_url ?? '';
    const refSrc = x.ref_source ?? '';
    const hasRef = refEv > 0;
    const modelErr = hasRef ? ((Math.abs(analytical - refEv) / refEv) * 100).toFixed(1) + '%' : '—';
    const ref_val = hasRef ? refEv.toFixed(3) : '—';
    const src = hasRef && refUrl
      ? `<a href="${refUrl}" target="_blank" rel="noopener" style="color:#4fc3f7">${refSrc}</a>`
      : (refSrc || '—');
    const mark = !hasRef ? '*' : (x.status === 'Error' ? '✗' : '✓');
    const egLabel = `${x.band_gap_ev.toFixed(3)} ${mark}`;
    const nameCell = x.name + verifyLink(refUrl, x.name + ' Band Gap', hasRef ? `E_g = ${refEv.toFixed(3)} eV (300K)` : '', refSrc);
    return [nameCell, egLabel, analytical.toFixed(3), ref_val, modelErr, src, x.gap_type, x.ionicity, renderGradientBar(x.tiered_coherence)];
  }));
  // Legend: headline is always the Phillips-Van Vechten model; mark reflects how it compares to experiment.
  const legendEl = document.querySelector('#bandgap-legend');
  if (!legendEl) {
    const lg = document.createElement('div');
    lg.id = 'bandgap-legend';
    lg.style.cssText = 'font-size:11px;color:#94a3b8;padding:4px 0;';
    lg.innerHTML = 'Value shown = Phillips-Van Vechten model. ✓ = within tolerance of experiment &nbsp;|&nbsp; ✗ = model outside tolerance &nbsp;|&nbsp; * = no experimental reference &nbsp;|&nbsp; Model Err = model vs experiment';
    const tbl = document.getElementById('t-bandgap');
    if (tbl) tbl.parentNode.insertBefore(lg, tbl.nextSibling);
  }
  makeChart('bandgap', r.map(x => x.name), [
    { label: 'E_g reported (eV)', data: r.map(x => x.band_gap_ev), backgroundColor: '#ce93d8' },
    { label: 'Analytical model (eV)', data: r.map(x => x.analytical_ev), backgroundColor: 'rgba(255,183,77,0.5)', borderColor: '#ffb74d', borderWidth: 1 },
    { label: 'Experimental ref (eV)', data: r.map(x => x.ref_gap_ev || null), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 }
  ], 'Material', 'E_g (eV)');

  if (r.length > 0) {
    const first = r[0];
    const geoDemo = geoNumDemo(first.band_gap_ev, 'Band Gap Energy (eV)')
      + geoNumDemo(first.ionicity, 'Ionicity')
      + geoNumDemo(first.covalency, 'Covalency');
    const demoEl = document.getElementById('geonum-demo-bandgap');
    if (demoEl) demoEl.innerHTML = '<h4>GeoNum Precision Tracking</h4>' + geoDemo;
  }

  // Dense gap landscape (model surface) + every computed material on it.
  buildBandgapField(r, elems);
  status('bandgap', `Done — ${r.length} results`);
  animData.bandgap = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('matp-bandgap', 'mat-bandgap', () => animData.bandgap, 'Band Gap Analysis');
}

function elasticScan() {
  status('elastic', 'Computing...'); const mats = JSON.parse(document.getElementById('in-elastic').value);
  const res = JSON.parse(run_elastic_scan(JSON.stringify({ materials: mats, precision: 0 })));
  const r = res.results;
  renderTable('elastic', ['Name', 'B (GPa)', 'Ref B', 'G (GPa)', 'Ref G', 'E (GPa)', 'Ref E', 'ν', 'Source', 'Coherence'], r.map(x => {
    const refB = x.ref_bulk ?? 0, refG = x.ref_shear ?? 0, refE = x.ref_youngs ?? 0;
    const refUrl = x.ref_url ?? '', refSrc = x.ref_source ?? '';
    const hasRef = refB > 0;
    const src = hasRef && refUrl ? `<a href="${refUrl}" target="_blank" style="color:#4fc3f7">${refSrc}</a>` : (refSrc || '—');
    const rb = hasRef ? refB.toFixed(0) : '—';
    const rg = hasRef ? refG.toFixed(0) : '—';
    const re = hasRef ? refE.toFixed(0) : '—';
    const mark = !hasRef ? '*' : (x.status === 'Error' ? '✗' : '✓');
    const bLabel = `${x.bulk_modulus.toFixed(1)} ${mark}`;
    const nameCell = x.name + verifyLink(refUrl, x.name + ' Elastic', hasRef ? `B=${refB} G=${refG} E=${refE} GPa` : '', refSrc);
    return [nameCell, bLabel, rb, x.shear_modulus.toFixed(1), rg, x.youngs_modulus.toFixed(1), re, x.poisson_ratio.toFixed(3), src, renderGradientBar(x.tiered_coherence)];
  }));
  makeChart('elastic', r.map(x => x.name), [
    { label: 'Bulk (GPa)', data: r.map(x => x.bulk_modulus), backgroundColor: '#ce93d8' },
    { label: 'Ref Bulk', data: r.map(x => x.ref_bulk || null), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
  ], 'Material', 'Modulus (GPa)');
  // Use all moduli: bulk (x) x shear (y) x Young (z), coloured by Poisson.
  set3DRich('elastic', r, {
    x: m => m.bulk_modulus, y: m => m.shear_modulus, z: m => m.youngs_modulus, c: m => m.poisson_ratio,
  }, 'Elastic moduli: bulk x shear x Young (colour = Poisson)');
  status('elastic', `Done — ${r.length} results`);
  animData.elastic = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('matp-elastic', 'mat-elastic', () => animData.elastic, 'Elastic Properties');
}

function phaseScan() {
  status('phase', 'Computing...'); const phases = JSON.parse(document.getElementById('in-phase').value);
  const res = JSON.parse(run_phase_scan(JSON.stringify({ phases, precision: 0 })));
  const r = res.results;
  renderTable('phase', ['Name', 'T_melt (K)', 'Ref T_melt', 'Error', 'Source', 'Stable Lo (K)', 'Stable Hi (K)', 'Coherence'], r.map(x => {
    const refMelt = x.ref_melting ?? 0, refUrl = x.ref_url ?? '', refSrc = x.ref_source ?? '';
    const hasRef = refMelt > 0;
    const err = hasRef ? ((Math.abs(x.melting_point - refMelt) / refMelt) * 100).toFixed(1) + '%' : '—';
    const ref_val = hasRef ? refMelt.toFixed(0) : '—';
    const src = hasRef && refUrl ? `<a href="${refUrl}" target="_blank" style="color:#4fc3f7">${refSrc}</a>` : (refSrc || '—');
    const mark = !hasRef ? '*' : (x.status === 'Error' ? '✗' : '✓');
    const tmLabel = `${x.melting_point.toFixed(0)} ${mark}`;
    const nameCell = x.name + verifyLink(refUrl, x.name + ' Phase', hasRef ? `T_melt = ${refMelt.toFixed(0)} K` : '', refSrc);
    return [nameCell, tmLabel, ref_val, err, src, x.stable_range[0], x.stable_range[1], renderGradientBar(x.tiered_coherence)];
  }));
  const datasets = r.map((x, i) => ({ label: x.name, data: x.gibbs_curve.map(p => ({ x: p.temp, y: p.gibbs })), borderColor: ['#ce93d8','#ab47bc','#7b1fa2','#e1bee7'][i % 4], fill: false, pointRadius: 0 }));
  makeChart('phase', null, datasets, 'Temperature (K)', 'Gibbs (kJ/mol)', 'scatter');
  // Dense: every phase's full Gibbs free-energy curve G(T) stacked in 3D.
  {
    const pts = [], vals = [];
    r.forEach((x, k) => { (x.gibbs_curve || []).forEach(pt => { const t = +pt.temp, g = +pt.gibbs; if (isFinite(t) && isFinite(g)) { pts.push(t, g, k); vals.push(g); } }); });
    if (pts.length >= 9) {
      window.__gdbs3dPayload = { points: new Float32Array(pts), values: vals, title: 'Phase Gibbs free energy G(T) by phase' };
      add3DButton('matp-phase', 'phase');
    } else {
      set3DPayload('phase', r.map(x => x.name), r.map(x => x.melting_point), 'Melting point T_melt (K)');
    }
  }
  status('phase', `Done — ${r.length} phases`);
  animData.phase = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('matp-phase', 'mat-phase', () => animData.phase, 'Phase Stability');
}

function thermalScan() {
  status('thermal', 'Computing...'); const mats = JSON.parse(document.getElementById('in-thermal').value);
  const res = JSON.parse(run_thermal_scan(JSON.stringify({ materials: mats, precision: 0 })));
  const r = res.results;
  renderTable('thermal', ['Name', '\u03BA_total (W/mK)', 'Ref \u03BA', 'Error', 'Source', '\u03BA_lattice', '\u03BA_elec', 'Coherence'], r.map(x => {
    const refK = x.ref_kappa ?? 0, refUrl = x.ref_url ?? '', refSrc = x.ref_source ?? '';
    const hasRef = refK > 0;
    const err = hasRef ? ((Math.abs(x.kappa_total - refK) / refK) * 100).toFixed(1) + '%' : '—';
    const ref_val = hasRef ? refK.toFixed(1) : '—';
    const src = hasRef && refUrl ? `<a href="${refUrl}" target="_blank" style="color:#4fc3f7">${refSrc}</a>` : (refSrc || '—');
    const mark = !hasRef ? '*' : (x.status === 'Error' ? '✗' : '✓');
    const kLabel = `${x.kappa_total.toFixed(1)} ${mark}`;
    const nameCell = x.name + verifyLink(refUrl, x.name + ' Thermal', hasRef ? `κ = ${refK.toFixed(1)} W/mK (300K)` : '', refSrc);
    return [nameCell, kLabel, ref_val, err, src, x.kappa_lattice.toFixed(1), x.kappa_electronic.toFixed(2), renderGradientBar(x.tiered_coherence)];
  }));
  makeChart('thermal', r.map(x => x.name), [
    { label: '\u03BA Total', data: r.map(x => x.kappa_total), backgroundColor: '#ce93d8' },
    { label: 'Ref \u03BA', data: r.map(x => x.ref_kappa || null), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
  ], 'Material', '\u03BA (W/mK)');
  // Every conductivity component as an independent axis: lattice x electronic x
  // total, coloured by the experimental reference. Uses all numeric columns the
  // scan produces rather than a single lifted bar.
  set3DRich('thermal', r, {
    x: m => m.kappa_lattice, y: m => m.kappa_electronic, z: m => m.kappa_total,
    c: m => (m.ref_kappa || m.kappa_total),
  }, 'Thermal conductivity: lattice x electronic x total (colour = reference)');
  status('thermal', `Done — ${r.length} results`);
  animData.thermal = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('matp-thermal', 'mat-thermal', () => animData.thermal, 'Thermal Conductivity');
}

function defectScan() {
  status('defect', 'Computing...'); const mats = JSON.parse(document.getElementById('in-defect').value);
  const res = JSON.parse(run_defect_scan(JSON.stringify({ materials: mats, precision: 0 })));
  const r = res.results;
  renderTable('defect', ['Name', 'E_v (eV)', 'Ref E_v', 'Error', 'Source', 'c_v', 'E_i (eV)', 'E_m (eV)', 'D (m\u00B2/s)', 'Coherence'], r.map(x => {
    const refV = x.ref_vacancy ?? 0, refUrl = x.ref_url ?? '', refSrc = x.ref_source ?? '';
    const hasRef = refV > 0;
    const err = hasRef ? ((Math.abs(x.vacancy_energy - refV) / refV) * 100).toFixed(1) + '%' : '—';
    const ref_val = hasRef ? refV.toFixed(2) : '—';
    const src = hasRef && refUrl ? `<a href="${refUrl}" target="_blank" style="color:#4fc3f7">${refSrc}</a>` : (refSrc || '—');
    const mark = !hasRef ? '*' : (x.status === 'Error' ? '✗' : '✓');
    const evLabel = `${x.vacancy_energy.toFixed(2)} ${mark}`;
    const nameCell = x.name + verifyLink(refUrl, x.name + ' Defect', hasRef ? `E_v = ${refV.toFixed(2)} eV` : '', refSrc);
    return [nameCell, evLabel, ref_val, err, src, x.vacancy_concentration.toExponential(2), x.interstitial_energy.toFixed(2), x.migration_energy.toFixed(2), x.diffusion_coeff.toExponential(2), renderGradientBar(x.tiered_coherence)];
  }));
  makeChart('defect', r.map(x => x.name), [
    { label: 'E_vacancy (eV)', data: r.map(x => x.vacancy_energy), backgroundColor: '#ce93d8' },
    { label: 'Ref E_v', data: r.map(x => x.ref_vacancy || null), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
    { label: 'E_migration (eV)', data: r.map(x => x.migration_energy), backgroundColor: '#ab47bc' },
  ], 'Material', 'Energy (eV)');
  // All defect energetics as independent axes: vacancy x interstitial x
  // migration energy, coloured by diffusivity (log10 D). Uses every numeric
  // column the scan produces rather than a single lifted bar.
  set3DRich('defect', r, {
    x: m => m.vacancy_energy, y: m => m.interstitial_energy, z: m => m.migration_energy,
    c: m => Math.log10(Math.max(m.diffusion_coeff || 1e-300, 1e-300)),
  }, 'Defect energetics: vacancy x interstitial x migration (colour = log10 D)');
  status('defect', `Done — ${r.length} results`);
  animData.defect = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('matp-defect', 'mat-defect', () => animData.defect, 'Defect Analysis');
}

// ============================================================
// Zone-safe GeoNum helpers
// initWasm() sets ZONES_MATERIALS (Angstrom-scale) globally.
// Superconductor/alloy calcs use eV/K values → need ZONES_THEORY (10^-35..10^30)
// ============================================================
function withTheoryZones(fn) {
  GeoNum.setZoneConfig(ZONES_THEORY.boundaries, ZONES_THEORY.minZone);
  try { return fn(); }
  finally { GeoNum.setZoneConfig(ZONES_MATERIALS.boundaries, ZONES_MATERIALS.minZone); }
}

// ============================================================
// GeoNum-tracked T_c computation
// GEOM_TC = σ₅² / (2+σ₅) → chain of mul/div across mismatched scales
// k_B = 8.617e-5 eV/K — dividing by this amplifies any drift → GeoNum essential
// ============================================================
function geoTc(ect_val) {
  return withTheoryZones(() => {
    const gS = geo(SIGMA5);                         // σ₅
    const gE = geo(ect_val);                        // E_CT (eV)
    const gK = geo(K_B_EV);                         // k_B (eV/K)
    const g2 = geo(2);
    // GEOM_TC = σ₅² / (2 + σ₅)
    const gGeomTc = gS.multiply(gS).divide(g2.add(gS));
    // T_c = GEOM_TC × E_CT / k_B
    const gTc = gGeomTc.multiply(gE).divide(gK);
    return {
      tc: gTc.toFloat64(),
      geomTc: gGeomTc.toFloat64(),
      drift: gTc.drift,
      unc: gTc.getUncertainty(),
      relUnc: gTc.getRelativeUncertainty(),
      zone: gTc.zone,
      shade: gTc.shade,
    };
  });
}

function geoDerivPanel(title, rows, geoMeta) {
  const metaHtml = geoMeta ? `
    <div class="deriv-row" style="margin-top:8px;border-top:1px solid var(--border);padding-top:6px">
      <span style="color:#4fc3f7">GeoNum Zone</span><span>${geoMeta.zone} | Shade ${geoMeta.shade}</span>
    </div>
    <div class="deriv-row"><span style="color:#4fc3f7">Drift</span><span>${geoMeta.drift.toFixed(4)} shades ${geoMeta.drift < 1.0 ? '&#x2713; stable' : '&#x26a0; drifting'}</span></div>
    <div class="deriv-row"><span style="color:#4fc3f7">Uncertainty</span><span>&plusmn;${geoMeta.unc.toExponential(3)} K (${(geoMeta.relUnc * 100).toExponential(2)}%)</span></div>
    <div class="deriv-note">GeoNum precision tracking active &mdash; drift &lt; 1.0 shade = HPC-grade stable</div>` : '';
  const rowsHtml = rows.map(([k, v, cls]) =>
    `<div class="deriv-row${cls ? ' ' + cls : ''}"><span>${k}</span><span>${v}</span></div>`).join('');
  return `<div class="deriv-block"><div class="deriv-title">${title}</div>${rowsHtml}${metaHtml}</div>`;
}

// ============================================================
// Superconductor Scan — σ₅ T_c prediction (GeoNum-tracked)
// ============================================================
function superconductorScan() {
  const mode = document.getElementById('sc-mode').value;
  status('superconductor', 'Computing...');
  const derivEl = document.getElementById('sc-derivation');

  if (mode === 'forward') {
    const ect = parseFloat(document.getElementById('sc-ect').value);
    const name = document.getElementById('sc-name').value.trim() || 'Custom';
    const { tc, geomTc: geomTcVal, drift, unc, relUnc, zone, shade } = geoTc(ect);
    renderTable('superconductor',
      ['Material', 'E_CT (eV)', 'T_c Predicted (K)', '\u00b1 (K)', 'O_primary', '\u03c3\u2085\u00b2/(2+\u03c3\u2085)'],
      [[name, ect.toFixed(3), tc.toFixed(2), unc.toExponential(3), O_PRIMARY.toFixed(5), geomTcVal.toFixed(6)]]
    );
    makeChart('superconductor', [name],
      [{ label: 'T_c (K)', data: [tc], backgroundColor: '#ce93d8' }],
      'Material', 'T_c (K)'
    );
    if (derivEl) derivEl.innerHTML = geoDerivPanel(
      '\u03c3\u2085 Superconductor Framework \u2014 GeoNum-tracked',
      [
        ['\u03c3\u2085', '0.09017 (Gaussian self-energy coefficient)'],
        ['O_primary', `= \u03c3\u2085/(2+\u03c3\u2085) = ${O_PRIMARY.toFixed(5)}`],
        ['GEOM_T\u2099', `= \u03c3\u2085\u00b2/(2+\u03c3\u2085) = ${geomTcVal.toFixed(6)}`],
        ['T_c', `= GEOM_T\u2099 \u00d7 E_CT / k_B`],
        [`T_c (${name})`, `${tc.toFixed(2)} K`, 'deriv-result'],
        ['Ceiling @ E_CT=2.5 eV', `${geoTc(2.5).tc.toFixed(0)} K`, ''],
      ],
      { drift, unc, relUnc, zone, shade }
    );
    animateCuO2Lattice('c-sc-lattice', tc);
    // Dense closed-form T_c(E_CT, sigma5) surface with this candidate marked on it.
    buildSuperconductorField([{ ect, sigma: SIGMA5, tc }],
      `Superconductor T_c landscape: ${name} marked at E_CT = ${ect.toFixed(3)} eV (T_c surface vs E_CT and sigma5)`);
    status('superconductor', `Done \u2014 T_c = ${tc.toFixed(2)} K \u00b1 ${unc.toExponential(2)} K`);

  } else if (mode === 'inverse') {
    const tc_target = parseFloat(document.getElementById('sc-tc-target').value);
    // E_CT = T_c × k_B / GEOM_TC — also tracked (ZONES_THEORY needed for eV/K scale)
    const { ect_req, unc, invDrift, invRelUnc, invZone, invShade } = withTheoryZones(() => {
      const gTcT = geo(tc_target);
      const gK   = geo(K_B_EV);
      const gS   = geo(SIGMA5);
      const g2   = geo(2);
      const gGeomTc = gS.multiply(gS).divide(g2.add(gS));
      const gEctReq = gTcT.multiply(gK).divide(gGeomTc);
      return {
        ect_req: gEctReq.toFloat64(),
        unc: gEctReq.getUncertainty(),
        invDrift: gEctReq.drift,
        invRelUnc: gEctReq.getRelativeUncertainty(),
        invZone: gEctReq.zone,
        invShade: gEctReq.shade,
      };
    });
    const note = ect_req > 2.8 ? '\u26a0 Near theoretical limit' : ect_req < 0.5 ? '\u26a0 Very low gap' : '\u2713 Feasible range';
    renderTable('superconductor',
      ['Target T_c (K)', 'Required E_CT (eV)', '\u00b1 (eV)', 'O_primary', 'Notes'],
      [[tc_target.toFixed(0), ect_req.toFixed(5), unc.toExponential(3), O_PRIMARY.toFixed(5), note]]
    );
    makeChart('superconductor', ['E_CT Required'],
      [{ label: 'E_CT (eV)', data: [ect_req], backgroundColor: '#4fc3f7' }],
      'Parameter', 'Energy (eV)'
    );
    if (derivEl) derivEl.innerHTML = geoDerivPanel(
      'Inverse Design \u2014 GeoNum-tracked',
      [
        ['E_CT', `= T_c \u00d7 k_B / GEOM_T\u2099 = ${ect_req.toFixed(5)} eV`, 'deriv-result'],
        ['Notes', note],
      ],
      { drift: invDrift, unc, relUnc: invRelUnc, zone: invZone, shade: invShade }
    );
    animateCuO2Lattice('c-sc-lattice', tc_target);
    // Same closed-form surface, with the solved (E_CT_req, target T_c) marked.
    buildSuperconductorField([{ ect: ect_req, sigma: SIGMA5, tc: tc_target }],
      `Superconductor T_c landscape: target T_c = ${tc_target.toFixed(0)} K needs E_CT = ${ect_req.toFixed(3)} eV (T_c surface vs E_CT and sigma5)`);
    status('superconductor', `Done \u2014 E_CT = ${ect_req.toFixed(5)} eV \u00b1 ${unc.toExponential(2)}`);

  } else {
    // Benchmark mode — GeoNum on every prediction
    const rows = KNOWN_CUPRATES.map(c => {
      const { tc: tc_pred, unc } = geoTc(c.ect);
      const err = ((tc_pred - c.tc_k) / c.tc_k * 100);
      return [c.name, c.formula, c.ect.toFixed(2), c.tc_k, tc_pred.toFixed(2), `\u00b1${unc.toExponential(2)}`, err.toFixed(1) + '%'];
    });
    renderTable('superconductor',
      ['Material', 'Formula', 'E_CT (eV)', 'T_c Meas (K)', 'T_c Pred (K)', 'GeoNum \u00b1', 'Error'],
      rows
    );
    makeChart('superconductor', KNOWN_CUPRATES.map(c => c.name), [
      { label: 'Measured', data: KNOWN_CUPRATES.map(c => c.tc_k), backgroundColor: '#a5d6a7' },
      { label: 'Predicted (\u03c3\u2085)', data: KNOWN_CUPRATES.map(c => geoTc(c.ect).tc), backgroundColor: '#ce93d8' },
    ], 'Cuprate', 'T_c (K)');
    const mae = rows.reduce((s, r) => s + Math.abs(parseFloat(r[6])), 0) / rows.length;
    const avgDrift = KNOWN_CUPRATES.reduce((s, c) => s + geoTc(c.ect).drift, 0) / KNOWN_CUPRATES.length;
    if (derivEl) derivEl.innerHTML = geoDerivPanel(
      '\u03c3\u2085 Framework Validation \u2014 GeoNum-tracked',
      [
        ['Single free parameter', '\u03c3\u2085 = 0.09017'],
        ['Cuprate families', KNOWN_CUPRATES.length.toString()],
        ['Mean absolute error', `${mae.toFixed(1)}%`, 'deriv-result'],
        ['Avg GeoNum drift', `${avgDrift.toFixed(4)} shades`, ''],
        ['Ceiling @ E_CT=2.5 eV', `${geoTc(2.5).tc.toFixed(0)} K`, ''],
      ],
      null
    );
    animateCuO2Lattice('c-sc-lattice', null);
    // Closed-form surface with every benchmark cuprate marked at its real
    // (E_CT, sigma5, predicted T_c) on the landscape.
    buildSuperconductorField(
      KNOWN_CUPRATES.map(c => ({ ect: c.ect, sigma: SIGMA5, tc: geoTc(c.ect).tc })),
      'Superconductor T_c landscape: sigma5 model surface with benchmark cuprates marked');
    status('superconductor', `Benchmark \u2014 ${KNOWN_CUPRATES.length} cuprates, MAE ${mae.toFixed(1)}%, avg drift ${avgDrift.toFixed(4)}`);
  }
}

// ============================================================
// Alloy Design Scan — σ₅ magnetic coupling (GeoNum-tracked)
// x_mole: ratio of small numbers at different scales → drift-sensitive
// ΔT_Curie: 4-value multiply chain → accumulates if IEEE 754 only
// ============================================================
function alloyDesignScan() {
  const mode = document.getElementById('al-mode').value;
  status('alloy', 'Computing...');
  const derivEl = document.getElementById('al-derivation');

  if (mode === 'forward') {
    const baseKey  = document.getElementById('al-base').value;
    const dopKey   = document.getElementById('al-dopant').value;
    const x_wt_pct = parseFloat(document.getElementById('al-conc').value);
    const x_wt = x_wt_pct / 100;
    const base = ALLOY_BASES[baseKey];
    const dop  = ALLOY_DOPANTS[dopKey];

    // GeoNum calcs use eV/K — switch to ZONES_THEORY for correct zone encoding
    const { x_mole, delta_TC, op, xmoleDrift, xmoleUnc, xmoleZone, xmoleShade,
            tcDrift, tcUnc, tcRelUnc, opVal } = withTheoryZones(() => {
      const gXwt   = geo(x_wt);
      const gMdop  = geo(dop.M_gmol);
      const gMbase = geo(base.M_gmol);
      const gNumer = gXwt.divide(gMdop);
      const gOneMinusXwt = geo(1 - x_wt);
      const gDenom = gNumer.add(gOneMinusXwt.divide(gMbase));
      const gXmole = gNumer.divide(gDenom);

      const gFrac  = geo(9).divide(geo(4));
      const gDelta = gFrac.multiply(gXmole).multiply(geo(dop.k_factor)).multiply(geo(base.tc0)).multiply(geo(-1));

      const gOp = geo(SIGMA5).divide(geo(2).add(geo(SIGMA5)));

      return {
        x_mole:     gXmole.toFloat64(),
        delta_TC:   gDelta.toFloat64(),
        opVal:      gOp.toFloat64(),
        xmoleDrift: gXmole.drift,
        xmoleUnc:   gXmole.getUncertainty(),
        xmoleZone:  gXmole.zone,
        xmoleShade: gXmole.shade,
        tcDrift:    gDelta.drift,
        tcUnc:      gDelta.getUncertainty(),
        tcRelUnc:   gDelta.getRelativeUncertainty(),
      };
    });
    const tc_curie = Math.max(0, base.tc0 + delta_TC);

    // Coercivity — uses GeoNum-derived O_primary (opVal)
    const hc  = base.hc0 + C_PIN * Math.pow(x_wt_pct, 2 / 3) * opVal * dop.E_pin_ev;

    // Resistivity (Matthiessen)
    const rho = base.rho0 + base.drho * x_wt_pct;

    renderTable('alloy',
      ['Property', 'Value', 'Unit', 'GeoNum / Notes'],
      [
        ['Base metal', base.name, '', base.crystal + ' structure'],
        ['Dopant', dop.name, '', dop.desc],
        ['Dopant wt%', x_wt_pct.toFixed(3), 'wt%', 'x_mole = ' + x_mole.toExponential(5)],
        ['x_mole drift', xmoleDrift.toFixed(4) + ' shades', '', xmoleDrift < 1 ? '\u2713 stable' : '\u26a0 drifting'],
        ['T_Curie', tc_curie.toFixed(1), 'K', '\u0394T = ' + delta_TC.toFixed(2) + ' \u00b1' + tcUnc.toFixed(2) + ' K'],
        ['H_c coercivity', hc.toFixed(0), 'A/m', '\u03c3\u2085 O-primary pinning'],
        ['\u03c1 resistivity', rho.toExponential(3), '\u03a9\u00b7m', 'Matthiessen rule'],
        ['O_primary', opVal.toFixed(6), '', '\u03c3\u2085/(2+\u03c3\u2085) GeoNum'],
      ]
    );
    makeChart('alloy',
      ['T_Curie / 10 (K)', 'H_c (A/m)', '\u03c1 (n\u03a9\u00b7m)'],
      [{ label: baseKey + ' + ' + x_wt_pct + '% ' + dopKey,
         data: [tc_curie / 10, hc, rho * 1e9],
         backgroundColor: ['#ce93d8', '#4fc3f7', '#a5d6a7'] }],
      'Property', 'Value'
    );
    if (derivEl) derivEl.innerHTML = geoDerivPanel(
      `\u03c3\u2085 Alloy Design \u2014 ${base.name} + ${x_wt_pct}% ${dop.name} (GeoNum-tracked)`,
      [
        ['x_mole', `${x_mole.toExponential(5)} \u00b1 ${xmoleUnc.toExponential(2)}`],
        ['\u0394T_Curie', `-(9/4) \u00d7 x_mole \u00d7 ${dop.k_factor} \u00d7 ${base.tc0} = ${delta_TC.toFixed(2)} K`],
        ['O_primary (GeoNum)', `${opVal.toFixed(6)}`],
        ['H_c', `H_c0 + C_pin \u00d7 x_wt%^(2/3) \u00d7 O_primary \u00d7 E_pin`],
        ['H_c result', `${hc.toFixed(0)} A/m`, 'deriv-result'],
        ['T_Curie result', `${tc_curie.toFixed(1)} K`, 'deriv-result'],
        ['Calibration', 'Fe + 0.2%C \u2192 H_c = 342 A/m (meas. 200\u2013400 A/m)'],
      ],
      { drift: Math.max(xmoleDrift, tcDrift), unc: tcUnc, relUnc: tcRelUnc, zone: xmoleZone, shade: xmoleShade }
    );
    animateBCCLattice('c-al-lattice', baseKey, dopKey, x_wt_pct, tc_curie);
    // Dense closed-form (wt% -> T_Curie, H_c) curve for this base+dopant pair,
    // with the chosen operating point marked on it.
    buildAlloyField([{ base, dop }], [{ xwt: x_wt_pct, base: baseKey, dop: dopKey }],
      `Alloy landscape: ${base.name} + ${dopKey} - T_Curie and H_c vs dopant wt% (point at ${x_wt_pct}%)`);
    status('alloy', `Done \u2014 T_Curie = ${tc_curie.toFixed(1)} K, H_c = ${hc.toFixed(0)} A/m, drift ${xmoleDrift.toFixed(4)}`);

  } else {
    // Inverse: target T_Curie → required dopant concentration (GeoNum-tracked)
    const tc_target = parseFloat(document.getElementById('al-tc-target').value);
    const baseKey   = document.getElementById('al-base-inv').value;
    const base      = ALLOY_BASES[baseKey];
    const invMarks = [];
    const rows = Object.entries(ALLOY_DOPANTS).map(([key, dop]) => {
      const delta_needed = base.tc0 - tc_target;
      if (delta_needed <= 0) return [dop.name, '0', base.hc0.toFixed(0), '\u2014', 'No depression needed'];
      const { xm, xmDrift } = withTheoryZones(() => {
        const gDelta = geo(delta_needed);
        const gDenom = geo(9).divide(geo(4)).multiply(geo(dop.k_factor)).multiply(geo(base.tc0));
        const gXmole = gDelta.divide(gDenom);
        return { xm: gXmole.toFloat64(), xmDrift: gXmole.drift };
      });
      const xwp = ((xm * dop.M_gmol) / (xm * dop.M_gmol + (1 - xm) * base.M_gmol)) * 100;
      const hc  = base.hc0 + C_PIN * Math.pow(xwp, 2 / 3) * O_PRIMARY * dop.E_pin_ev;
      invMarks.push({ xwt: xwp, base: baseKey, dop: key });
      return [dop.name, xwp.toFixed(4), hc.toFixed(0), `drift:${xmDrift.toFixed(3)}`, xwp > 5 ? '\u26a0 High' : '\u2713 OK'];
    });
    renderTable('alloy', ['Dopant', 'Required wt%', 'H_c (A/m)', 'GeoNum Drift', 'Notes'], rows);
    makeChart('alloy',
      Object.values(ALLOY_DOPANTS).map(d => d.name),
      [{ label: 'Required wt%', data: rows.map(r => parseFloat(r[1]) || 0), backgroundColor: '#ce93d8' }],
      'Dopant', 'Required wt%'
    );
    if (derivEl) derivEl.innerHTML = geoDerivPanel(
      `Inverse Design \u2014 ${base.name} \u2192 T_Curie = ${tc_target} K (GeoNum-tracked)`,
      [
        ['Base T_Curie', `${base.tc0} K`],
        ['\u0394T needed', `${(base.tc0 - tc_target).toFixed(0)} K`, 'deriv-result'],
      ], null
    );
    animateBCCLattice('c-al-lattice', baseKey, 'C', 0.2, tc_target);
    // Dense (wt% -> T_Curie, H_c) curves for every candidate dopant in this base,
    // with each solved required-concentration operating point marked.
    buildAlloyField(
      Object.values(ALLOY_DOPANTS).map(dop => ({ base, dop })),
      invMarks,
      `Alloy landscape: ${base.name} candidate dopants - T_Curie and H_c vs wt% (targets for T_Curie = ${tc_target} K marked)`);
    status('alloy', `Done \u2014 ${Object.keys(ALLOY_DOPANTS).length} dopant options for T_Curie = ${tc_target} K`);
  }
}

// ============================================================
// Canvas: CuO₂ lattice — Cooper pair cooperative wave
// ============================================================
function animateCuO2Lattice(canvasId, tc) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  if (animHandles[canvasId]) cancelAnimationFrame(animHandles[canvasId]);
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const spacing = 58;
  const cols = Math.floor((W - 20) / spacing) + 1;
  const rows = Math.floor((H - 40) / spacing) + 1;
  const offX = (W - (cols - 1) * spacing) / 2;
  const offY = 36 + (H - 36 - (rows - 1) * spacing) / 2;
  let frame = 0;

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = window.cpal().panel;
    ctx.fillRect(0, 0, W, H);
    frame++;
    const waveSpeed = 0.04;
    const t = frame * waveSpeed;

    ctx.lineWidth = 1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = offX + c * spacing, y = offY + r * spacing;
        const phase = (c + r) * 0.6;
        const wave = 0.4 + 0.4 * Math.sin(t - phase);
        ctx.strokeStyle = `rgba(80,120,220,${0.2 + 0.2 * wave})`;
        if (c < cols - 1) { ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + spacing, y); ctx.stroke(); }
        if (r < rows - 1) { ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + spacing); ctx.stroke(); }
      }
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = offX + c * spacing, y = offY + r * spacing;
        const phase = (c + r) * 0.8;
        const wave = 0.5 + 0.5 * Math.sin(t - phase);
        const alpha = 0.55 + 0.35 * wave;
        if (c < cols - 1) {
          const ox = x + spacing / 2, oy = y;
          ctx.beginPath(); ctx.arc(ox, oy, 6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(240,70,50,${alpha})`; ctx.fill();
          ctx.strokeStyle = `rgba(255,140,120,${alpha * 0.7})`; ctx.lineWidth = 1; ctx.stroke();
          ctx.fillStyle = `rgba(255,190,170,${alpha})`; ctx.font = '7px monospace'; ctx.textAlign = 'center';
          ctx.fillText('O\u00b2\u207b', ox, oy - 9);
        }
        if (r < rows - 1) {
          const ox = x, oy = y + spacing / 2;
          ctx.beginPath(); ctx.arc(ox, oy, 6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(240,70,50,${alpha})`; ctx.fill();
          ctx.strokeStyle = `rgba(255,140,120,${alpha * 0.7})`; ctx.lineWidth = 1; ctx.stroke();
          ctx.fillStyle = `rgba(255,190,170,${alpha})`; ctx.font = '7px monospace'; ctx.textAlign = 'center';
          ctx.fillText('O\u00b2\u207b', ox + 18, oy);
        }
      }
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = offX + c * spacing, y = offY + r * spacing;
        const phase = (c * 1.7 + r * 1.3) * 0.5;
        const wave = 0.5 + 0.5 * Math.sin(t - phase);
        const glow = 4 + 8 * wave;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, glow + 10);
        grad.addColorStop(0, `rgba(70,140,255,${0.5 * wave})`);
        grad.addColorStop(1, 'rgba(70,140,255,0)');
        ctx.beginPath(); ctx.arc(x, y, glow + 10, 0, Math.PI * 2);
        ctx.fillStyle = grad; ctx.fill();
        ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(60,120,255,${0.8 + 0.2 * wave})`; ctx.fill();
        ctx.strokeStyle = '#90c0ff'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = '#d0e8ff'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
        ctx.fillText('Cu\u00b2\u207a', x, y + 3);
      }
    }

    ctx.fillStyle = 'rgba(30,40,70,0.85)';
    ctx.fillRect(0, 0, W, 30);
    ctx.fillStyle = 'rgba(160,210,255,0.9)';
    ctx.font = '11px monospace'; ctx.textAlign = 'left';
    ctx.fillText('CuO\u2082 Plane \u2014 Cooperative Cooper Pair Formation', 8, 12);
    if (tc) {
      ctx.fillStyle = 'rgba(100,255,180,0.9)';
      ctx.fillText(`T\u2099 = ${tc.toFixed(1)} K  |  \u03c3\u2085 = 0.09017  |  O_primary = ${O_PRIMARY.toFixed(4)}`, 8, 24);
    }
    animHandles[canvasId] = requestAnimationFrame(draw);
  }
  draw();
}

// ============================================================
// Canvas: BCC lattice — Fe/Ni/Co + dopant pinning
// ============================================================
function animateBCCLattice(canvasId, baseKey, dopKey, x_wt_pct, tc_curie) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  if (animHandles[canvasId]) cancelAnimationFrame(animHandles[canvasId]);
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const crystal = ALLOY_BASES[baseKey]?.crystal || 'BCC';

  const cx = W / 2 + 10, cy = H / 2 + 10;
  const aSize = 85;
  const iso = (px, py, pz) => ({
    px: cx + (px - pz) * aSize * 0.65,
    py: cy + (px + pz) * aSize * 0.32 - py * aSize * 0.58,
  });

  const BCC_CORNERS = [
    [0,0,0],[1,0,0],[0,1,0],[1,1,0],
    [0,0,1],[1,0,1],[0,1,1],[1,1,1],
  ];
  const BCC_CENTER = [0.5, 0.5, 0.5];
  const INTERSTITIALS = [
    [0.5,0,0],[0,0.5,0],[0,0,0.5],
    [1,0.5,0],[0.5,1,0],[1,0,0.5],
    [0,1,0.5],[0.5,0,1],[0,0.5,1],
    [1,0.5,1],[0.5,1,1],[1,1,0.5],
  ];
  const EDGES = [
    [0,1],[2,3],[4,5],[6,7],[0,2],[1,3],[4,6],[5,7],[0,4],[1,5],[2,6],[3,7]
  ];

  const numDoped = Math.min(Math.max(Math.round(x_wt_pct * 8), 0), INTERSTITIALS.length);
  const dopedSites = INTERSTITIALS.slice(0, numDoped);
  let frame = 0;

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, W, H);
    frame++;
    const t = frame * 0.016;
    const theta = t * 0.35; // slow Y-axis rotation

    const rot = (px, py, pz) => {
      const rx = px - 0.5, ry = py - 0.5, rz = pz - 0.5;
      return iso(rx * Math.cos(theta) + rz * Math.sin(theta) + 0.5, ry + 0.5, -rx * Math.sin(theta) + rz * Math.cos(theta) + 0.5);
    };

    const wallY = cy + 12 * Math.sin(t * 0.5);
    const dwGrad = ctx.createLinearGradient(0, wallY - 40, 0, wallY + 40);
    dwGrad.addColorStop(0, 'rgba(255,200,50,0)');
    dwGrad.addColorStop(0.5, 'rgba(255,200,50,0.06)');
    dwGrad.addColorStop(1, 'rgba(255,200,50,0)');
    ctx.fillStyle = dwGrad; ctx.fillRect(0, wallY - 40, W, 80);

    ctx.strokeStyle = 'rgba(130,180,255,0.25)'; ctx.lineWidth = 1;
    for (const [a, b] of EDGES) {
      const p1 = rot(...BCC_CORNERS[a]), p2 = rot(...BCC_CORNERS[b]);
      ctx.beginPath(); ctx.moveTo(p1.px, p1.py); ctx.lineTo(p2.px, p2.py); ctx.stroke();
    }

    for (const site of dopedSites) {
      const p = rot(...site);
      const pulse = 0.6 + 0.4 * Math.sin(t * 2.5 + site[0] * 2.8 + site[2] * 1.9);
      ctx.beginPath(); ctx.arc(p.px, p.py, 5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(80,210,110,${pulse})`; ctx.fill();
      ctx.strokeStyle = `rgba(140,255,160,${pulse * 0.8})`; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = `rgba(180,255,190,${pulse * 0.9})`; ctx.font = '7px monospace'; ctx.textAlign = 'center';
      ctx.fillText(dopKey, p.px, p.py - 7);
    }

    for (const corner of BCC_CORNERS) {
      const p = rot(...corner);
      const w = 0.7 + 0.3 * Math.sin(t + corner[0] * 2.1 + corner[2] * 1.7);
      ctx.beginPath(); ctx.arc(p.px, p.py, 10, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,155,45,${w * 0.9})`; ctx.fill();
      ctx.strokeStyle = '#ffc870'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.fillStyle = '#fff0cc'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText(baseKey, p.px, p.py + 3);
    }

    {
      const p = rot(...BCC_CENTER);
      const w = 0.7 + 0.3 * Math.sin(t * 1.4 + 1.0);
      const g = ctx.createRadialGradient(p.px, p.py, 0, p.px, p.py, 20);
      g.addColorStop(0, `rgba(255,175,55,${w * 0.65})`); g.addColorStop(1, 'rgba(255,175,55,0)');
      ctx.beginPath(); ctx.arc(p.px, p.py, 20, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
      ctx.beginPath(); ctx.arc(p.px, p.py, 12, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,155,45,${w})`; ctx.fill();
      ctx.strokeStyle = '#ffc870'; ctx.lineWidth = 2; ctx.stroke();
      ctx.fillStyle = '#fff0cc'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText(baseKey, p.px, p.py + 3);
    }

    ctx.fillStyle = 'rgba(30,40,70,0.85)'; ctx.fillRect(0, 0, W, 30);
    ctx.fillStyle = 'rgba(160,210,255,0.9)'; ctx.font = '11px monospace'; ctx.textAlign = 'left';
    ctx.fillText(`${baseKey} ${crystal} + ${x_wt_pct.toFixed(2)}% ${dopKey} \u2014 Magnetic Domain Pinning`, 8, 12);
    if (tc_curie) {
      ctx.fillStyle = 'rgba(100,255,180,0.9)';
      ctx.fillText(`T_Curie = ${tc_curie.toFixed(0)} K  |  O_primary = ${O_PRIMARY.toFixed(4)}`, 8, 24);
    }
    ctx.fillStyle = 'rgba(255,155,45,0.9)'; ctx.font = '9px monospace';
    ctx.fillText(`\u25cf ${baseKey} atom`, 8, H - 28);
    if (numDoped > 0) { ctx.fillStyle = 'rgba(80,210,110,0.9)'; ctx.fillText(`\u25cf ${dopKey} (pinning site)`, 8, H - 16); }
    ctx.fillStyle = 'rgba(255,200,50,0.5)'; ctx.fillText('\u25ac Domain wall', numDoped > 0 ? 120 : 8, H - 16);

    animHandles[canvasId] = requestAnimationFrame(draw);
  }
  draw();
}
